import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  GitBranch,
  ClipboardList,
  LifeBuoy,
  CalendarClock,
  Wrench,
  ArrowUpRight,
  TrendingUp,
  Circle,
  AlertTriangle,
  Phone,
  MessageCircle,
  Mail,
  FileText,
  Send,
  Calendar as CalendarIcon,
  Target,
  Activity,
  Zap,
  GripVertical,
  Settings as SettingsIcon,
  Eye,
  EyeOff,
  Timer,
  Gauge,
  RotateCcw,
} from "lucide-react";
import UnresolvedWidget from "../components/dashboard/UnresolvedWidget";
import MyWeekWidget from "../components/dashboard/MyWeekWidget";
import HidroReflectionPrompt from "../components/HidroReflectionPrompt";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
  pointerWithin,
} from "@dnd-kit/core";
import api from "../lib/api";
import PhaseChip from "../components/PhaseChip";
import JobCardRow from "../components/JobCardRow";
import MapEmbed from "../components/MapEmbed";
import Modal from "../components/Modal";
import { useAuth } from "../context/AuthContext";
import { usePersistedState } from "../lib/persisted";
import { LEAD_PHASE_LABELS, JOBCARD_PHASE_LABELS } from "../lib/enums";

const STAT_DEFS = [
  { key: "leads_active", label: "Active leads", icon: GitBranch, to: "/leads", color: "#1FB6C1", perm: "nav.leads" },
  { key: "jobs_scheduled", label: "Scheduled", icon: CalendarClock, to: "/jobcards", color: "#3B82F6", perm: "nav.jobcards" },
  { key: "jobs_in_progress", label: "In progress", icon: Wrench, to: "/jobcards", color: "#A78BFA", perm: "nav.jobcards" },
  { key: "tickets_open", label: "Open tickets", icon: LifeBuoy, to: "/tickets", color: "#F59E0B", perm: "nav.tickets" },
  { key: "customers_active", label: "Customers", icon: Users, to: "/customers", color: "#10B981", perm: "nav.customers" },
  { key: "upcoming_services", label: "Services 7d", icon: CalendarClock, to: "/scheduler", color: "#EC4899", perm: "nav.schedule" },
];

// Maps each dashboard widget id (from the fold-below-hero list) to the
// permission required to render it. Widgets not listed here are
// universally visible (e.g. `my-week`, `my-tasks`).
const WIDGET_PERMISSIONS = {
  "unresolved": "nav.tickets",
  "avg-resolve": "nav.tickets",
  "qte-conversion": "nav.jobcards",
  "activity-volumes": "nav.customers",
  "overdue-services": "nav.schedule",
  "followups": "nav.leads",
  "lead-funnel": "nav.leads",
  "job-pipeline": "nav.jobcards",
  "recent-activity": "nav.calls",
  "hot-tickets": "nav.tickets",
  "upcoming-jobs": "nav.jobcards",
  "top-customers": "nav.customers",
};

export default function Dashboard() {
  const { user, hasPermission } = useAuth();
  const [stats, setStats] = useState({});
  const [tasks, setTasks] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [calls, setCalls] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [leads, setLeads] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [customiseOpen, setCustomiseOpen] = useState(false);

  useEffect(() => {
    (async () => {
      // Resilient fetch — any 403 / 404 falls back to [] so the dashboard
      // still renders for reps/technicians even when some endpoints are
      // off-limits to their role.
      const safe = (p) => p.catch(() => ({ data: [] }));
      const [s, t, e, c, tk, l, j, cu, fu] = await Promise.all([
        safe(api.get("/dashboard/stats")),
        safe(api.get("/tasks")),
        safe(api.get("/equipment")),
        safe(api.get("/calls")),
        safe(api.get("/tickets")),
        safe(api.get("/leads")),
        safe(api.get("/jobcards")),
        safe(api.get("/customers")),
        safe(api.get("/followups")),
      ]);
      setStats(s.data && typeof s.data === "object" && !Array.isArray(s.data) ? s.data : {});
      const arr = (v) => (Array.isArray(v) ? v : []);
      setTasks(arr(t.data));
      setEquipment(arr(e.data));
      setCalls(arr(c.data));
      setTickets(arr(tk.data));
      setLeads(arr(l.data));
      setJobs(arr(j.data));
      setCustomers(arr(cu.data));
      setFollowups(arr(fu.data));
    })();
  }, []);

  const customerName = (cid) => customers.find((c) => c.id === cid)?.name || "Unknown";

  const totalActive =
    (stats.leads_active || 0) + (stats.jobs_scheduled || 0) + (stats.jobs_in_progress || 0);

  return (
    <div className="space-y-5" data-testid="dashboard">
      <HidroReflectionPrompt />
      {/* Slim hero */}
      <div className="relative overflow-hidden rounded-xl border border-white/10 px-5 py-4 md:px-6 md:py-5 bg-gradient-to-br from-[#1fb6c1]/10 via-[#0a0b0e] to-[#A78BFA]/10">
        <div className="absolute -top-20 -right-12 w-56 h-56 rounded-full bg-[#1fb6c1]/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#647087]">
              Hi {user?.name?.split(" ")[0] || "there"} ·{" "}
              {new Date().toLocaleDateString('en-GB', { weekday: "long", month: "long", day: "numeric" })}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-0.5 flex items-center gap-3">
              Dashboard
              <span className="text-xs font-normal text-[#a0aabf] inline-flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-white font-bold">{totalActive}</span> moving
              </span>
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCustomiseOpen(true)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="dashboard-customise-btn"
              title="Show / hide widgets"
            >
              <SettingsIcon className="w-3.5 h-3.5" /> Customise
            </button>
            <Link to="/leads" className="hv-btn hv-btn-ghost text-xs" data-testid="dashboard-go-leads">
              Pipeline <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/jobcards" className="hv-btn hv-btn-primary text-xs" data-testid="dashboard-go-jobs">
              Jobs <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* KPI strip — compact */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
        {STAT_DEFS.filter((d) => hasPermission(d.perm)).map((d) => (
          <Link
            key={d.key}
            to={d.to}
            data-testid={`stat-${d.key}`}
            className="relative hv-card px-3 py-3 group transition overflow-hidden"
            style={{ borderTop: `2px solid ${d.color}` }}
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition pointer-events-none"
              style={{ background: `linear-gradient(135deg, ${d.color}10 0%, transparent 60%)` }}
            />
            <div className="relative flex items-center justify-between">
              <div
                className="w-7 h-7 rounded-md flex items-center justify-center"
                style={{ background: `${d.color}1f`, border: `1px solid ${d.color}55` }}
              >
                <d.icon className="w-3.5 h-3.5" strokeWidth={2} style={{ color: d.color }} />
              </div>
              <ArrowUpRight className="w-3 h-3 text-[#647087] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
            </div>
            <div
              className="relative mt-2 font-display font-bold text-2xl md:text-3xl tracking-tight leading-none"
              style={{ color: d.color }}
            >
              {stats[d.key] ?? "–"}
            </div>
            <div className="relative text-[10px] uppercase tracking-wider text-[#647087] mt-1">
              {d.label}
            </div>
          </Link>
        ))}
      </div>

      {/* Widget grid — draggable & user-customisable per logged-in user.
          Widgets filtered by `WIDGET_PERMISSIONS` so reps/technicians only
          see tiles tied to data surfaces their role can actually read. */}
      <DraggableWidgetGrid
        widgets={[
          ...((["admin", "manager", "owner"].includes(user?.role)) ? [{ id: "unresolved", label: "Unresolved data", render: () => <UnresolvedWidget /> }] : []),
          { id: "my-week", label: "My week", render: () => <MyWeekWidget /> },
          { id: "my-tasks", label: "My tasks", render: () => <MyTasksWidget user={user} tasks={tasks} /> },
          { id: "avg-resolve", label: "Avg ticket resolve time", render: () => <AvgResolveTimeWidget tickets={tickets} /> },
          { id: "qte-conversion", label: "Quote → Job Card conversion", render: () => <ConversionWidget leads={leads} jobs={jobs} /> },
          { id: "activity-volumes", label: "Activity volumes", render: () => <ActivityVolumesWidget leads={leads} customers={customers} tickets={tickets} jobs={jobs} /> },
          { id: "overdue-services", label: "Overdue services", render: () => <OverdueServicesWidget equipment={equipment} customerName={customerName} /> },
          { id: "followups", label: "Follow-ups due", render: () => <FollowupsWidget followups={followups} customerName={customerName} /> },
          { id: "lead-funnel", label: "Lead funnel", render: () => <LeadFunnelWidget leads={leads} /> },
          { id: "job-pipeline", label: "Job pipeline", render: () => <JobPipelineWidget jobs={jobs} /> },
          { id: "recent-activity", label: "Recent activity", render: () => <RecentActivityWidget calls={calls} customerName={customerName} /> },
          { id: "hot-tickets", label: "Hot tickets", render: () => <HotTicketsWidget tickets={tickets} customerName={customerName} /> },
          { id: "upcoming-jobs", label: "This week", render: () => <UpcomingJobsWidget jobs={jobs} customerName={customerName} /> },
          { id: "top-customers", label: "Top customers", render: () => <TopCustomersWidget customers={customers} jobs={jobs} /> },
        ].filter((w) => !WIDGET_PERMISSIONS[w.id] || hasPermission(WIDGET_PERMISSIONS[w.id]))}
        customiseOpen={customiseOpen}
        onCloseCustomise={() => setCustomiseOpen(false)}
      />

      {hasPermission("nav.customers") && <MapEmbed height={300} />}
    </div>
  );
}

/* ============================================================================
 *  Draggable widget grid — per-user persisted order
 * ========================================================================== */

function DraggableWidgetGrid({ widgets, customiseOpen, onCloseCustomise }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );
  const [persistedOrder, setPersistedOrder] = usePersistedState(
    "dashboard.widgetOrder",
    [],
  );
  const [hiddenWidgets, setHiddenWidgets] = usePersistedState(
    "dashboard.hiddenWidgets",
    [],
  );
  const hiddenSet = useMemo(() => new Set(hiddenWidgets), [hiddenWidgets]);
  const [activeId, setActiveId] = useState(null);

  // Effective order: honour persisted ids first, append any new widgets at the end.
  const order = useMemo(() => {
    const known = widgets.map((w) => w.id);
    const seen = new Set();
    const out = [];
    persistedOrder.forEach((id) => {
      if (known.includes(id) && !seen.has(id)) {
        out.push(id);
        seen.add(id);
      }
    });
    known.forEach((id) => {
      if (!seen.has(id)) out.push(id);
    });
    return out;
  }, [widgets, persistedOrder]);

  const visibleOrder = useMemo(
    () => order.filter((id) => !hiddenSet.has(id)),
    [order, hiddenSet],
  );

  const widgetById = useMemo(() => Object.fromEntries(widgets.map((w) => [w.id, w])), [widgets]);

  const onDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const fromIdx = order.indexOf(String(active.id));
    const toIdx = order.indexOf(String(over.id));
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...order];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setPersistedOrder(next);
  };

  const toggleVisible = (id) => {
    setHiddenWidgets(
      hiddenSet.has(id)
        ? hiddenWidgets.filter((x) => x !== id)
        : [...hiddenWidgets, id],
    );
  };

  const resetAll = () => {
    setHiddenWidgets([]);
    setPersistedOrder([]);
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={({ active }) => setActiveId(String(active.id))}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div
          className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 lg:auto-rows-fr gap-3 md:gap-4"
          data-testid="dashboard-widget-grid"
        >
          {visibleOrder.map((id) => {
            const w = widgetById[id];
            if (!w) return null;
            return (
              <DraggableWidget key={id} id={id} dimmed={activeId === id}>
                {w.render()}
              </DraggableWidget>
            );
          })}
          {visibleOrder.length === 0 && (
            <div className="col-span-full hv-card p-8 text-center text-sm text-[#647087]">
              No widgets visible. Click <span className="text-[#1FB6C1] font-bold">Customise</span> to bring some back.
            </div>
          )}
        </div>
        <DragOverlay dropAnimation={null}>
          {activeId ? (
            <div className="hv-card px-3 py-2 shadow-2xl shadow-[#1FB6C1]/30 border-[#1FB6C1]/60 max-w-md cursor-grabbing">
              <div className="text-xs uppercase tracking-wider text-[#1FB6C1]">
                Reordering widget…
              </div>
              <div className="text-sm font-medium truncate capitalize">
                {activeId.replaceAll("-", " ")}
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {customiseOpen && (
        <Modal
          open
          onClose={onCloseCustomise}
          title="Customise dashboard"
          testId="dashboard-customise-modal"
          maxWidth="max-w-lg"
        >
          <div className="space-y-3" data-testid="dashboard-customise-content">
            <div className="text-xs text-[#a0aabf]">
              Show or hide widgets to focus on what matters today. Drag to
              reorder them on the dashboard. Settings are saved per-user on this device.
            </div>
            <div className="space-y-1.5">
              {order.map((id) => {
                const w = widgetById[id];
                if (!w) return null;
                const isVisible = !hiddenSet.has(id);
                return (
                  <button
                    type="button"
                    key={id}
                    onClick={() => toggleVisible(id)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded border transition text-sm ${
                      isVisible
                        ? "border-[#1FB6C1]/40 bg-[#1FB6C1]/10 text-white"
                        : "border-white/10 bg-white/[0.02] text-[#647087] hover:border-white/20"
                    }`}
                    data-testid={`dashboard-toggle-${id}`}
                  >
                    <span className="font-medium capitalize">
                      {w.label || id.replaceAll("-", " ")}
                    </span>
                    {isVisible ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#1FB6C1] font-bold">
                        <Eye className="w-3 h-3" /> Visible
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#647087] font-bold">
                        <EyeOff className="w-3 h-3" /> Hidden
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-white/5">
              <button
                type="button"
                onClick={resetAll}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="dashboard-customise-reset"
              >
                <RotateCcw className="w-3 h-3" /> Reset to default
              </button>
              <button
                type="button"
                onClick={onCloseCustomise}
                className="hv-btn hv-btn-primary text-xs"
                data-testid="dashboard-customise-done"
              >
                Done
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function DraggableWidget({ id, dimmed, children }) {
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id });
  const {
    setNodeRef: setDragRef,
    attributes,
    listeners,
    isDragging,
  } = useDraggable({ id });

  // Merge the drop ref with a wrapping ref node
  const setRef = (node) => {
    setDropRef(node);
  };

  return (
    <div
      ref={setRef}
      className={`relative group transition ${
        isOver ? "ring-2 ring-[#1FB6C1] rounded-md" : ""
      } ${dimmed || isDragging ? "opacity-30" : ""}`}
      data-testid={`dashboard-widget-slot-${id}`}
    >
      {/* Drag handle, floats top-right of the widget header */}
      <button
        ref={setDragRef}
        {...attributes}
        {...listeners}
        type="button"
        className="absolute top-2 right-2 z-10 text-[#647087] hover:text-[#1FB6C1] cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition"
        title="Drag to reorder"
        data-testid={`dashboard-widget-drag-${id}`}
        aria-label="Drag widget to reorder"
        onClick={(e) => e.preventDefault()}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      {children}
    </div>
  );
}

/* ============================================================================
 *  Widgets
 * ========================================================================== */

function WidgetCard({ title, icon: Icon, accent = "#1FB6C1", to, ctaLabel, children, emptyHint, count, testId }) {
  const isEmpty = count === 0;
  // Mobile: empty cards shrink to a tight strip (no min-h), populated cards grow up to
  // a max-h so they can't run away. Desktop keeps the fixed h-[320px] grid alignment.
  const sizeClass = isEmpty
    ? "md:h-[320px]"
    : "max-h-[420px] md:h-[320px] md:max-h-none";
  return (
    <section
      className={`hv-card p-4 flex flex-col overflow-hidden ${sizeClass}`}
      data-testid={testId}
    >
      <header className="flex items-center justify-between gap-2 mb-2 md:mb-3 shrink-0 pr-6">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && (
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
              style={{ background: `${accent}1f`, border: `1px solid ${accent}55`, color: accent }}
            >
              <Icon className="w-3.5 h-3.5" />
            </div>
          )}
          <h3 className="font-display text-sm font-bold tracking-tight truncate">{title}</h3>
          {count !== undefined && (
            <span
              className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded-full border shrink-0"
              style={{
                borderColor: `${accent}40`,
                color: accent,
                background: `${accent}0d`,
              }}
            >
              {count}
            </span>
          )}
        </div>
        {to && (
          <Link
            to={to}
            className="text-[10px] uppercase tracking-wider hover:underline shrink-0 inline-flex items-center gap-0.5"
            style={{ color: accent }}
          >
            {ctaLabel || "All"} <ArrowUpRight className="w-3 h-3" />
          </Link>
        )}
      </header>
      <div className={`${isEmpty ? "" : "flex-1 min-h-0"} overflow-y-auto -mx-1 px-1 hv-scroll`}>
        {isEmpty ? (
          <div className="text-[11px] text-[#647087] italic py-1 md:py-4 text-center">
            {emptyHint || "Nothing here yet."}
          </div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

/* -------------------- My tasks (today / active for me) -------------------- */
function MyTasksWidget({ user, tasks }) {
  const my = useMemo(() => {
    if (!user) return [];
    const active = tasks.filter(
      (t) =>
        (t.assigned_to_ids || []).includes(user.id) &&
        t.status !== "completed" &&
        t.status !== "rejected" &&
        t.status !== "cancelled",
    );
    const prio = { urgent: 0, high: 1, normal: 2, low: 3 };
    active.sort(
      (a, b) =>
        (prio[a.priority] ?? 9) - (prio[b.priority] ?? 9) ||
        new Date(b.created_at || 0) - new Date(a.created_at || 0),
    );
    return active;
  }, [tasks, user]);

  return (
    <WidgetCard
      title="My tasks"
      icon={ClipboardList}
      accent="#A78BFA"
      to="/tasks"
      count={my.length}
      emptyHint="All clear — no active tasks."
      testId="widget-my-tasks"
    >
      <ul className="space-y-1.5">
        {my.slice(0, 5).map((t) => (
          <li
            key={t.id}
            className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border border-white/5 bg-white/[0.02] hover:border-[#A78BFA]/40"
            data-testid={`widget-my-task-${t.id}`}
          >
            {t.status === "in_progress" ? (
              <CalendarClock className="w-3 h-3 text-blue-400 shrink-0" />
            ) : (
              <Circle className="w-3 h-3 text-[#647087] shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{t.title}</div>
              {t.description && (
                <div className="text-[10px] text-[#647087] truncate leading-tight">
                  {t.description}
                </div>
              )}
            </div>
            <span
              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold shrink-0"
              style={{
                background: priorityColor(t.priority) + "1a",
                color: priorityColor(t.priority),
                border: `1px solid ${priorityColor(t.priority)}55`,
              }}
            >
              {t.priority}
            </span>
          </li>
        ))}
        {my.length > 5 && (
          <Link
            to="/tasks"
            className="block text-[10px] uppercase tracking-wider text-[#647087] hover:text-[#A78BFA] text-center pt-1"
          >
            +{my.length - 5} more →
          </Link>
        )}
      </ul>
    </WidgetCard>
  );
}

function priorityColor(p) {
  return { urgent: "#F43F5E", high: "#F59E0B", normal: "#1FB6C1", low: "#647087" }[p] || "#1FB6C1";
}

/* -------------------- Overdue services -------------------- */
function OverdueServicesWidget({ equipment, customerName }) {
  const overdue = useMemo(() => {
    const now = Date.now();
    return equipment
      .filter(
        (eq) =>
          eq.is_service_item &&
          eq.next_service_date &&
          new Date(eq.next_service_date).getTime() < now,
      )
      .sort((a, b) => new Date(a.next_service_date) - new Date(b.next_service_date));
  }, [equipment]);
  return (
    <WidgetCard
      title="Overdue services"
      icon={AlertTriangle}
      accent="#F43F5E"
      to="/scheduler"
      count={overdue.length}
      emptyHint="No equipment overdue. Nice."
      testId="widget-overdue-services"
    >
      <ul className="space-y-1.5">
        {overdue.slice(0, 5).map((eq) => {
          const d = new Date(eq.next_service_date);
          const daysOverdue = Math.floor((Date.now() - d) / (1000 * 60 * 60 * 24));
          return (
            <li
              key={eq.id}
              className="flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded border border-rose-500/20 bg-rose-500/[0.04] hover:border-rose-500/40"
              data-testid={`widget-overdue-${eq.id}`}
            >
              <Link
                to={`/customers/${eq.customer_id}`}
                className="min-w-0 flex-1"
              >
                <div className="font-medium truncate">{eq.name}</div>
                <div className="text-[10px] text-[#647087] truncate">
                  {customerName(eq.customer_id)}
                </div>
              </Link>
              <span className="text-[10px] font-mono text-rose-300 shrink-0">
                {daysOverdue}d
              </span>
            </li>
          );
        })}
        {overdue.length > 5 && (
          <Link
            to="/scheduler"
            className="block text-[10px] uppercase tracking-wider text-[#647087] hover:text-rose-300 text-center pt-1"
          >
            +{overdue.length - 5} more →
          </Link>
        )}
      </ul>
    </WidgetCard>
  );
}

/* -------------------- Follow-ups due now -------------------- */
function FollowupsWidget({ followups, customerName }) {
  const due = useMemo(() => {
    const now = Date.now();
    return followups
      .filter((f) => f.status === "due_now" || (f.scheduled_for && new Date(f.scheduled_for).getTime() <= now && f.status !== "sent" && f.status !== "skipped"))
      .sort((a, b) => new Date(a.scheduled_for || 0) - new Date(b.scheduled_for || 0));
  }, [followups]);
  return (
    <WidgetCard
      title="Follow-ups due"
      icon={Target}
      accent="#06B6D4"
      to="/followups"
      count={due.length}
      emptyHint="Nothing pending."
      testId="widget-followups"
    >
      <ul className="space-y-1.5">
        {due.slice(0, 5).map((f) => (
          <li
            key={f.id}
            className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border border-cyan-500/20 bg-cyan-500/[0.04] hover:border-cyan-500/40"
          >
            <span className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold shrink-0">
              {f.kind || "—"}
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{f.subject || f.body?.slice(0, 40) || "Follow-up"}</div>
              <div className="text-[10px] text-[#647087] truncate">
                {customerName(f.customer_id)}
              </div>
            </div>
          </li>
        ))}
        {due.length > 5 && (
          <Link
            to="/followups"
            className="block text-[10px] uppercase tracking-wider text-[#647087] hover:text-cyan-300 text-center pt-1"
          >
            +{due.length - 5} more →
          </Link>
        )}
      </ul>
    </WidgetCard>
  );
}

/* -------------------- Lead funnel (mini horizontal bars) -------------------- */
function LeadFunnelWidget({ leads }) {
  const phases = useMemo(() => {
    const counts = {};
    const open = leads.filter((l) => !["won", "lost", "lead_paused", "future_prospect"].includes(l.phase));
    open.forEach((l) => {
      counts[l.phase] = (counts[l.phase] || 0) + 1;
    });
    const arr = Object.entries(counts)
      .map(([k, v]) => ({
        key: k,
        label: LEAD_PHASE_LABELS[k] || k.replaceAll("_", " "),
        count: v,
      }))
      .sort((a, b) => b.count - a.count);
    return arr;
  }, [leads]);
  const max = phases[0]?.count || 1;
  const total = phases.reduce((s, p) => s + p.count, 0);

  return (
    <WidgetCard
      title="Lead funnel"
      icon={GitBranch}
      accent="#1FB6C1"
      to="/leads"
      count={total}
      emptyHint="No open leads."
      testId="widget-lead-funnel"
    >
      <ul className="space-y-1.5">
        {phases.slice(0, 6).map((p) => (
          <li key={p.key} className="text-[10px]">
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <span className="text-[#dde3ec] truncate">{p.label}</span>
              <span className="font-mono text-[#1fb6c1] shrink-0">{p.count}</span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#1fb6c1] to-[#06b6d4] rounded-full transition-all"
                style={{ width: `${(p.count / max) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </WidgetCard>
  );
}

/* -------------------- Job pipeline (mini bars) -------------------- */
function JobPipelineWidget({ jobs }) {
  const phases = useMemo(() => {
    const counts = {};
    const open = jobs.filter(
      (j) => !["closed", "cancelled", "paid", "complete", "signed_off"].includes(j.phase),
    );
    open.forEach((j) => {
      counts[j.phase] = (counts[j.phase] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([k, v]) => ({
        key: k,
        label: JOBCARD_PHASE_LABELS[k] || k.replaceAll("_", " "),
        count: v,
      }))
      .sort((a, b) => b.count - a.count);
  }, [jobs]);
  const max = phases[0]?.count || 1;
  const total = phases.reduce((s, p) => s + p.count, 0);

  return (
    <WidgetCard
      title="Job pipeline"
      icon={Wrench}
      accent="#F59E0B"
      to="/jobcards"
      count={total}
      emptyHint="No open jobs."
      testId="widget-job-pipeline"
    >
      <ul className="space-y-1.5">
        {phases.slice(0, 6).map((p) => (
          <li key={p.key} className="text-[10px]">
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <span className="text-[#dde3ec] truncate">{p.label}</span>
              <span className="font-mono text-amber-300 shrink-0">{p.count}</span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all"
                style={{ width: `${(p.count / max) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </WidgetCard>
  );
}

/* -------------------- Recent activity (calls) -------------------- */
const TYPE_ICON = {
  call: Phone,
  whatsapp: MessageCircle,
  sms: Send,
  email: Mail,
  meeting: CalendarIcon,
  note: FileText,
};
function RecentActivityWidget({ calls, customerName }) {
  const recent = useMemo(
    () => [...calls].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 6),
    [calls],
  );
  return (
    <WidgetCard
      title="Recent activity"
      icon={Activity}
      accent="#10B981"
      to="/calls"
      count={calls.length}
      emptyHint="No activity logged yet."
      testId="widget-recent-activity"
    >
      <ul className="space-y-1.5">
        {recent.map((c) => {
          const Icon = TYPE_ICON[c.type] || Phone;
          return (
            <li
              key={c.id}
              className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border border-white/5 bg-white/[0.02]"
              data-testid={`widget-activity-${c.id}`}
            >
              <Icon
                className={`w-3 h-3 shrink-0 ${
                  c.direction === "in" ? "text-cyan-400" : "text-emerald-400"
                }`}
              />
              <Link
                to={`/customers/${c.customer_id}`}
                className="min-w-0 flex-1 hover:text-[#1fb6c1]"
              >
                <div className="font-medium truncate">
                  {customerName(c.customer_id)}
                </div>
                <div className="text-[10px] text-[#647087] truncate">
                  {c.notes || `${c.type} · ${c.by_user_name || "—"}`}
                </div>
              </Link>
              <span className="text-[10px] text-[#647087] shrink-0 font-mono">
                {timeAgo(c.created_at)}
              </span>
            </li>
          );
        })}
      </ul>
    </WidgetCard>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString('en-GB');
}

/* -------------------- Hot tickets -------------------- */
function HotTicketsWidget({ tickets, customerName }) {
  const hot = useMemo(() => {
    return tickets
      .filter((t) =>
        ["escalated", "logged", "pending_assignment", "assigned", "in_progress", "open"].includes(
          t.status,
        ),
      )
      .sort((a, b) => {
        const rank = { escalated: 0, in_progress: 1, assigned: 2, logged: 3, pending_assignment: 4, open: 5 };
        return (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
      });
  }, [tickets]);
  return (
    <WidgetCard
      title="Hot tickets"
      icon={Zap}
      accent="#F43F5E"
      to="/tickets"
      count={hot.length}
      emptyHint="No open tickets."
      testId="widget-hot-tickets"
    >
      <ul className="space-y-1.5">
        {hot.slice(0, 5).map((t) => (
          <li
            key={t.id}
            className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border border-white/5 bg-white/[0.02] hover:border-rose-500/40"
          >
            <Link
              to={`/tickets/${t.id}`}
              className="min-w-0 flex-1"
              data-testid={`widget-ticket-${t.id}`}
            >
              <div className="font-medium truncate">
                {t.short_id ? <span className="text-[#1fb6c1] font-mono">#{t.short_id}</span> : null} {t.subject}
              </div>
              <div className="text-[10px] text-[#647087] truncate">
                {customerName(t.customer_id)}
              </div>
            </Link>
            <PhaseChip value={t.status} className="text-[9px]" />
          </li>
        ))}
        {hot.length > 5 && (
          <Link
            to="/tickets"
            className="block text-[10px] uppercase tracking-wider text-[#647087] hover:text-rose-300 text-center pt-1"
          >
            +{hot.length - 5} more →
          </Link>
        )}
      </ul>
    </WidgetCard>
  );
}

/* -------------------- Upcoming jobs (this week) -------------------- */
function UpcomingJobsWidget({ jobs, customerName }) {
  const upcoming = useMemo(() => {
    const now = Date.now();
    const week = now + 7 * 24 * 60 * 60 * 1000;
    return jobs
      .filter(
        (j) =>
          j.scheduled_at &&
          new Date(j.scheduled_at).getTime() >= now &&
          new Date(j.scheduled_at).getTime() <= week &&
          !["closed", "cancelled", "paid"].includes(j.phase),
      )
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  }, [jobs]);
  return (
    <WidgetCard
      title="This week"
      icon={CalendarClock}
      accent="#3B82F6"
      to="/calendar"
      ctaLabel="Calendar"
      count={upcoming.length}
      emptyHint="No jobs scheduled this week."
      testId="widget-upcoming-jobs"
    >
      <ul className="space-y-1.5">
        {upcoming.slice(0, 5).map((j) => (
          <li key={j.id}>
            <JobCardRow
              job={j}
              customer={j.customer_id ? { id: j.customer_id, name: customerName(j.customer_id) } : null}
              compact
              showProgress={false}
              accent="#3B82F6"
              testIdPrefix="dashboard-upcoming-job"
            />
          </li>
        ))}
      </ul>
    </WidgetCard>
  );
}

/* -------------------- Top customers (by job count) -------------------- */
function TopCustomersWidget({ customers, jobs }) {
  const top = useMemo(() => {
    const counts = {};
    jobs.forEach((j) => {
      if (j.customer_id) counts[j.customer_id] = (counts[j.customer_id] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([cid, count]) => ({
        customer: customers.find((c) => c.id === cid),
        count,
      }))
      .filter((x) => x.customer)
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [customers, jobs]);

  return (
    <WidgetCard
      title="Top customers"
      icon={Users}
      accent="#10B981"
      to="/customers"
      count={top.length}
      emptyHint="No customer activity yet."
      testId="widget-top-customers"
    >
      <ul className="space-y-1.5">
        {top.map((x) => (
          <li
            key={x.customer.id}
            className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border border-white/5 bg-white/[0.02] hover:border-emerald-500/40"
          >
            <Link to={`/customers/${x.customer.id}`} className="min-w-0 flex-1">
              <div className="font-medium truncate">{x.customer.name}</div>
              {x.customer.external_ref && (
                <div className="text-[10px] font-mono text-[#647087]">
                  {x.customer.external_ref}
                </div>
              )}
            </Link>
            <span className="text-[10px] font-mono text-emerald-300 shrink-0">
              {x.count} jobs
            </span>
          </li>
        ))}
      </ul>
    </WidgetCard>
  );
}


/* -------------------- KPI tile (used inside metric widgets) -------------------- */
function MetricTile({ value, label, accent = "#1FB6C1", suffix = "" }) {
  return (
    <div
      className="rounded-md p-3 border flex items-baseline gap-2"
      style={{
        background: `${accent}0d`,
        borderColor: `${accent}33`,
      }}
    >
      <div
        className="font-display font-bold text-3xl leading-none tabular-nums"
        style={{ color: accent }}
      >
        {value}
      </div>
      {suffix && (
        <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>
          {suffix}
        </div>
      )}
      <div className="text-[10px] uppercase tracking-wider text-[#a0aabf] ml-auto text-right leading-tight">
        {label}
      </div>
    </div>
  );
}

/* -------------------- Avg ticket resolve time -------------------- */
const RESOLVED_STATUSES = new Set(["resolved", "resolved_via_jc", "resolved_external", "closed"]);

function AvgResolveTimeWidget({ tickets }) {
  const { avgHours, samples, distribution, fastest, slowest } = useMemo(() => {
    const resolved = tickets.filter(
      (t) => RESOLVED_STATUSES.has(t.status) && t.created_at,
    );
    const durations = resolved
      .map((t) => {
        const start = new Date(t.created_at).getTime();
        const endIso = t.closed_at || t.resolved_at || t.updated_at;
        if (!endIso) return null;
        const end = new Date(endIso).getTime();
        const ms = end - start;
        if (!Number.isFinite(ms) || ms < 0) return null;
        return { t, hours: ms / (1000 * 60 * 60) };
      })
      .filter(Boolean);
    if (durations.length === 0) {
      return { avgHours: 0, samples: 0, distribution: [], fastest: null, slowest: null };
    }
    const avgHours =
      durations.reduce((s, d) => s + d.hours, 0) / durations.length;
    // Distribution buckets: <4h, 4-24h, 1-3d, 3-7d, >7d
    const buckets = [
      { label: "<4h", max: 4, count: 0 },
      { label: "4-24h", max: 24, count: 0 },
      { label: "1-3d", max: 72, count: 0 },
      { label: "3-7d", max: 168, count: 0 },
      { label: ">7d", max: Infinity, count: 0 },
    ];
    durations.forEach(({ hours }) => {
      const b = buckets.find((x) => hours < x.max);
      if (b) b.count++;
    });
    const sorted = [...durations].sort((a, b) => a.hours - b.hours);
    return {
      avgHours,
      samples: durations.length,
      distribution: buckets,
      fastest: sorted[0],
      slowest: sorted[sorted.length - 1],
    };
  }, [tickets]);

  const fmt = (h) => {
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 48) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
  };
  const max = Math.max(1, ...distribution.map((b) => b.count));

  return (
    <WidgetCard
      title="Avg resolve time"
      icon={Timer}
      accent="#06B6D4"
      to="/tickets"
      count={samples}
      emptyHint="No resolved tickets yet — close some to see your team's pace."
      testId="widget-avg-resolve"
    >
      <div className="space-y-3">
        <MetricTile
          value={samples ? fmt(avgHours) : "–"}
          label={`across ${samples} resolved`}
          accent="#06B6D4"
        />
        {samples > 0 && (
          <>
            <ul className="space-y-1.5">
              {distribution.map((b) => (
                <li key={b.label} className="text-[10px]">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className="text-[#dde3ec]">{b.label}</span>
                    <span className="font-mono text-cyan-300 shrink-0">{b.count}</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-500 to-cyan-300 rounded-full transition-all"
                      style={{ width: `${(b.count / max) * 100}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
            <div className="grid grid-cols-2 gap-1.5 text-[10px]">
              {fastest && (
                <div className="px-2 py-1.5 rounded border border-emerald-500/30 bg-emerald-500/5">
                  <div className="text-[9px] uppercase tracking-wider text-emerald-300 font-bold">
                    Fastest
                  </div>
                  <div className="text-emerald-200 font-mono">{fmt(fastest.hours)}</div>
                  <div className="text-[#647087] truncate">{fastest.t.subject}</div>
                </div>
              )}
              {slowest && (
                <div className="px-2 py-1.5 rounded border border-rose-500/30 bg-rose-500/5">
                  <div className="text-[9px] uppercase tracking-wider text-rose-300 font-bold">
                    Slowest
                  </div>
                  <div className="text-rose-200 font-mono">{fmt(slowest.hours)}</div>
                  <div className="text-[#647087] truncate">{slowest.t.subject}</div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </WidgetCard>
  );
}

/* -------------------- Quote → Job Card conversion --------------------
   Tracks how many leads that REACHED a quote stage in the funnel
   ended up converting to a Job Card for that customer.

   "Reached quote stage" = lead phase is at or beyond design_quote.
*/
const QUOTED_LEAD_PHASES = new Set([
  // Current funnel — Quoting group
  "design_quote", "estimate_sent", "estimate_followup", "so_sent", "so_followup",
  // Closed group: lead must have passed through quote
  "won", "lead_paused",
  // Legacy phases (AppSheet migration)
  "quote_in_progress", "quote_sent", "sales_order_sent",
  "design_in_progress", "future_prospect",
]);

function ConversionWidget({ leads, jobs }) {
  const { rate, quoted, converted, byMonth } = useMemo(() => {
    const quotedLeads = leads.filter((l) => QUOTED_LEAD_PHASES.has(l.phase));
    // Customers that have at least one job card
    const customersWithJC = new Set(jobs.map((j) => j.customer_id).filter(Boolean));
    const convertedLeads = quotedLeads.filter(
      (l) => l.customer_id && customersWithJC.has(l.customer_id),
    );
    const rate =
      quotedLeads.length > 0
        ? (convertedLeads.length / quotedLeads.length) * 100
        : 0;
    // Monthly bars (last 6 months) — by lead created_at
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
      months.push({
        key,
        label: d.toLocaleDateString('en-GB', { month: "short" }),
        quoted: 0,
        converted: 0,
      });
    }
    const monthOf = (iso) => {
      if (!iso) return null;
      const d = new Date(iso);
      return `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
    };
    quotedLeads.forEach((l) => {
      const k = monthOf(l.created_at);
      const m = months.find((x) => x.key === k);
      if (m) m.quoted += 1;
    });
    convertedLeads.forEach((l) => {
      const k = monthOf(l.created_at);
      const m = months.find((x) => x.key === k);
      if (m) m.converted += 1;
    });
    return {
      rate,
      quoted: quotedLeads.length,
      converted: convertedLeads.length,
      byMonth: months,
    };
  }, [leads, jobs]);

  const max = Math.max(1, ...byMonth.map((m) => m.quoted));

  return (
    <WidgetCard
      title="Quote → Job Card"
      icon={Gauge}
      accent="#A78BFA"
      to="/leads"
      count={quoted}
      emptyHint="No quoted leads yet."
      testId="widget-qte-conversion"
    >
      <div className="space-y-3">
        <MetricTile
          value={`${rate.toFixed(1)}`}
          suffix="%"
          label={`${converted} of ${quoted} quoted leads converted`}
          accent="#A78BFA"
        />
        {quoted > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">
              Last 6 months · quoted vs converted
            </div>
            <div className="flex items-end gap-1.5 h-20">
              {byMonth.map((m) => (
                <div
                  key={m.key}
                  className="flex-1 flex flex-col items-center justify-end gap-0.5"
                  title={`${m.label}: ${m.quoted} quoted / ${m.converted} converted to JC`}
                >
                  <div className="flex items-end gap-0.5 h-full w-full justify-center">
                    <div
                      className="w-[6px] rounded-t bg-gradient-to-t from-violet-700 to-[#A78BFA]"
                      style={{ height: `${(m.quoted / max) * 100}%`, minHeight: m.quoted ? "2px" : "0" }}
                    />
                    <div
                      className="w-[6px] rounded-t bg-gradient-to-t from-emerald-700 to-emerald-400"
                      style={{ height: `${(m.converted / max) * 100}%`, minHeight: m.converted ? "2px" : "0" }}
                    />
                  </div>
                  <div className="text-[9px] text-[#647087]">{m.label}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 text-[9px] text-[#647087] mt-1">
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-[#A78BFA]" /> Quoted
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-emerald-400" /> Converted to JC
              </span>
            </div>
          </div>
        )}
      </div>
    </WidgetCard>
  );
}

/* -------------------- Activity volumes (last 7d / 30d / 365d) -------------------- */
function ActivityVolumesWidget({ leads, customers, tickets, jobs }) {
  const buckets = useMemo(() => {
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    const ranges = [
      { key: "7d", label: "Last 7 days", days: 7 },
      { key: "30d", label: "Last 30 days", days: 30 },
      { key: "365d", label: "Last 12 months", days: 365 },
    ];
    // For each entity, prefer the real business date (migrated from AppSheet)
    // and fall back to created_at when the business date is missing.
    const dateOf = {
      leads: (x) => x.first_contact_date || x.created_at,
      customers: (x) => x.created_at,
      tickets: (x) => x.time_logged || x.created_at,
      jobs: (x) => x.scheduled_at || x.created_at,
    };
    const countWithin = (arr, days, getter) =>
      arr.filter((x) => {
        const iso = getter(x);
        if (!iso) return false;
        const t = new Date(iso).getTime();
        return Number.isFinite(t) && now - t <= days * day;
      }).length;
    return ranges.map((r) => ({
      ...r,
      leads: countWithin(leads, r.days, dateOf.leads),
      customers: countWithin(customers, r.days, dateOf.customers),
      tickets: countWithin(tickets, r.days, dateOf.tickets),
      jobs: countWithin(jobs, r.days, dateOf.jobs),
    }));
  }, [leads, customers, tickets, jobs]);

  const ROW = [
    { key: "leads", label: "Leads", accent: "#1FB6C1", icon: GitBranch, to: "/leads" },
    { key: "customers", label: "Customers", accent: "#10B981", icon: Users, to: "/customers" },
    { key: "tickets", label: "Tickets", accent: "#F43F5E", icon: LifeBuoy, to: "/tickets" },
    { key: "jobs", label: "Job cards", accent: "#F59E0B", icon: Wrench, to: "/jobcards" },
  ];

  // Find the max value across rows for relative bar widths within each window
  const maxByWindow = buckets.reduce((acc, b) => {
    acc[b.key] = Math.max(1, ...ROW.map((r) => b[r.key]));
    return acc;
  }, {});

  return (
    <WidgetCard
      title="Activity volumes"
      icon={Activity}
      accent="#10B981"
      count={ROW.reduce((s, r) => s + buckets[2][r.key], 0)}
      emptyHint="No activity logged yet."
      testId="widget-activity-volumes"
    >
      <div className="space-y-3" data-testid="activity-volumes-content">
        {/* Window tabs */}
        <ActivityTabs buckets={buckets} ROW={ROW} maxByWindow={maxByWindow} />
      </div>
    </WidgetCard>
  );
}

function ActivityTabs({ buckets, ROW, maxByWindow }) {
  const [activeKey, setActiveKey] = useState("30d");
  const active = buckets.find((b) => b.key === activeKey) || buckets[1];
  const max = maxByWindow[active.key];

  return (
    <>
      <div className="flex gap-1 p-0.5 rounded-md border border-white/5 bg-white/[0.02]" data-testid="activity-volumes-tabs">
        {buckets.map((b) => (
          <button
            key={b.key}
            type="button"
            onClick={() => setActiveKey(b.key)}
            className={`flex-1 text-[10px] uppercase tracking-wider font-bold py-1 rounded transition ${
              activeKey === b.key
                ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                : "text-[#647087] hover:text-[#dde3ec]"
            }`}
            data-testid={`activity-volumes-tab-${b.key}`}
          >
            {b.label}
          </button>
        ))}
      </div>
      <ul className="space-y-1.5">
        {ROW.map((r) => {
          const v = active[r.key];
          const Icon = r.icon;
          return (
            <li key={r.key} data-testid={`activity-volumes-row-${r.key}`}>
              <Link
                to={r.to}
                className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
              >
                <div
                  className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
                  style={{ background: `${r.accent}1f`, border: `1px solid ${r.accent}55`, color: r.accent }}
                >
                  <Icon className="w-3 h-3" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className="text-[#dde3ec] font-medium">{r.label}</span>
                    <span
                      className="font-mono font-bold tabular-nums shrink-0"
                      style={{ color: r.accent }}
                    >
                      {v}
                    </span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(v / max) * 100}%`,
                        background: `linear-gradient(to right, ${r.accent}cc, ${r.accent})`,
                      }}
                    />
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </>
  );
}
